import type { Page, Product, Order, ShippingInfo, StoreConfig, PaymentMethodsResponse, PaymentMethodType } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'Terjadi kesalahan',
        },
      }))
      
      throw new ApiError(
        error.error?.message || 'Terjadi kesalahan',
        response.status,
        error.error?.code || 'UNKNOWN_ERROR',
      )
    }

    return response.json()
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }
    throw new ApiError('Tidak dapat terhubung ke server', 0, 'NETWORK_ERROR')
  }
}

// Cache for resolved username from custom domain
let resolvedUsername: string | null = null

/**
 * Get store username from subdomain or custom domain
 * 
 * Domain Rules:
 * - Free tier: username.amplifier.id (subdomain only)
 * - Premium/Businessman: username.amplifier.id OR custom domain
 * 
 * Resolution order:
 * 1. Local development: ?store=username query param
 * 2. Subdomain: username.amplifier.id → extract username
 * 3. Custom domain: mybusiness.com → resolve via API
 */
async function resolveStoreUsername(): Promise<string> {
  const hostname = window.location.hostname
  
  // For local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search)
    return params.get('store') || 'demo'
  }
  
  // For subdomain: username.amplifier.id
  if (hostname.endsWith('.amplifier.id')) {
    const username = hostname.split('.')[0]
    // Skip if it's a system subdomain
    if (!['www', 'api', 'dashboard', 'store'].includes(username)) {
      return username
    }
  }
  
  // For custom domain, resolve via API
  if (resolvedUsername) {
    return resolvedUsername
  }
  
  try {
    const response = await fetchApi<{
      type: 'subdomain' | 'custom'
      username: string
      businessName: string
      domain: string
    }>(`/domain/resolve/${hostname}`)
    
    resolvedUsername = response.username
    return response.username
  } catch (error) {
    console.error('Failed to resolve domain:', error)
    // Fallback: use hostname as username (won't work but prevents crash)
    return hostname.split('.')[0]
  }
}

/**
 * Synchronous version - returns cached value or extracts from subdomain
 * Use this for immediate access, but prefer resolveStoreUsername() for accuracy
 */
function getStoreUsername(): string {
  // Return cached username if available
  if (resolvedUsername) {
    return resolvedUsername
  }
  
  const hostname = window.location.hostname
  
  // For local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search)
    return params.get('store') || 'demo'
  }
  
  // For subdomain: username.amplifier.id
  if (hostname.endsWith('.amplifier.id')) {
    const username = hostname.split('.')[0]
    if (!['www', 'api', 'dashboard', 'store'].includes(username)) {
      return username
    }
  }
  
  // For custom domain without cache, return hostname (will be resolved async)
  return hostname.split('.')[0]
}

export const api = {
  // Initialize store - resolve username for custom domains
  async init(): Promise<string> {
    return resolveStoreUsername()
  },

  // Get published page for store
  async getPublishedPage(slug: string = 'home'): Promise<Page> {
    const username = await resolveStoreUsername()
    const response = await fetchApi<{ page: Page } | Page>(`/pages/public/${username}/${slug}`)
    // Handle both wrapped and unwrapped responses
    return 'page' in response ? response.page : response
  },

  // Get all active products for store
  async getProducts(): Promise<Product[]> {
    const username = await resolveStoreUsername()
    const response = await fetchApi<{ products: Product[]; total: number } | Product[]>(`/products/public/${username}`)
    // Handle both wrapped and unwrapped responses
    return 'products' in response ? response.products : response
  },

  // Get single product
  async getProduct(productId: string): Promise<Product> {
    const username = await resolveStoreUsername()
    const response = await fetchApi<{ product: Product } | Product>(`/products/public/${username}/${productId}`)
    // Handle both wrapped and unwrapped responses
    return 'product' in response ? response.product : response
  },

  // Get store configuration
  async getStoreConfig(): Promise<StoreConfig> {
    const username = await resolveStoreUsername()
    const response = await fetchApi<{ config: StoreConfig } | StoreConfig>(`/store/config/${username}`)
    // Handle both wrapped and unwrapped responses
    return 'config' in response ? response.config : response
  },

  // Create order
  async createOrder(data: {
    items: Array<{ productId: string; quantity: number }>
    shippingInfo: ShippingInfo
    paymentMethodType?: PaymentMethodType
    paymentMethodFee?: number
    grandTotal?: number
  }): Promise<Order> {
    const username = await resolveStoreUsername()
    
    // Transform shippingInfo to flat structure expected by backend
    const orderPayload = {
      items: data.items,
      customerName: data.shippingInfo.customerName,
      customerPhone: data.shippingInfo.customerPhone,
      customerEmail: data.shippingInfo.customerEmail,
      customerAddress: data.shippingInfo.customerAddress,
      paymentMethodType: data.paymentMethodType,
      paymentMethodFee: data.paymentMethodFee,
      grandTotal: data.grandTotal,
    }
    
    const response = await fetchApi<{ data?: { order: Order }; order?: Order } | Order>(`/orders/public/${username}`, {
      method: 'POST',
      body: JSON.stringify(orderPayload),
    })
    // Handle various response formats
    if ('data' in response && response.data?.order) {
      return response.data.order
    }
    if ('order' in response && response.order) {
      return response.order
    }
    return response as Order
  },

  // Get order details
  async getOrder(orderId: string): Promise<Order> {
    const response = await fetchApi<{ order: Order } | Order>(`/orders/public/${orderId}`)
    // Handle both wrapped and unwrapped responses
    return 'order' in response ? response.order : response
  },

  // Get available payment methods with fees
  async getPaymentMethods(amount: number): Promise<PaymentMethodsResponse> {
    const response = await fetchApi<{ data: PaymentMethodsResponse } | PaymentMethodsResponse>(
      `/payment-methods?amount=${amount}`
    )
    return 'data' in response ? response.data : response
  },

  // Calculate payment fee for a specific method
  async calculatePaymentFee(amount: number, paymentMethod: PaymentMethodType): Promise<{
    subtotal: number
    paymentFee: number
    ppn: number
    grandTotal: number
    paymentMethod: PaymentMethodType
    paymentMethodName: string
    feeBreakdown: string
  }> {
    const response = await fetchApi<{ data: any }>('/payment-methods/calculate', {
      method: 'POST',
      body: JSON.stringify({ amount, paymentMethod }),
    })
    return response.data
  },
}

export { ApiError }
export { getStoreUsername, resolveStoreUsername }
